import { FC, useRef } from "react";

import { CardGrid } from "@/components/Layout/BaseLayouts/CardGrid/CardGrid";
import { CockpitAbosSection } from "@/components/Portal/Cockpit/CockpitAbosSection/CockpitAbosSection.tsx";
import { CockpitPurchaseIntentSection } from "@/components/Portal/Cockpit/CockpitPurchaseIntentSection/CockpitPurchaseIntentSection.tsx";
import { CockpitTodosSection } from "@/components/Portal/Cockpit/CockpitTodosSection/CockpitTodosSection.tsx";
import { EVNCard } from "@/components/Portal/Cockpit/EVNCard/EVNCard.tsx";
import { FWBCard } from "@/components/Portal/Cockpit/FWBCard/FWBCard.tsx";
import { AddDeviceTile } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/AddDeviceTile/AddDeviceTile.tsx";
import { createIdentificationStatusQueryOptions } from "@/hooks/identificationStatusQueryOptions.ts";
import { useFWBMemberStatus } from "@/hooks/useFWBMemberStatus.ts";
import { isKycVerified } from "@/hooks/useNeedsIDCheck.ts";
import { usePrefetchPortalSimList } from "@/hooks/usePrefetchPortalSimList.ts";
import { portalRoutes } from "@/router/routes/portalRoutes.ts";
import {
  useGetCustomer,
  useGetEvnForCustomer,
  useGetIdentificationStatus,
} from "@/services/api.ts";
import { isBusinessCustomer } from "@/utils/customerUtils.ts";
import { hasAvailableEvns } from "@/utils/evnUtils.ts";

/**
 * The Cockpit dashboard: open to-dos, the baskets waiting to be activated, a
 * preview of the customer's Abos and the promo cards, in that order.
 */
export const CockpitDashboard: FC = () => {
  const staleTime = useRef(0);
  const { data: identificationStatus } = useGetIdentificationStatus({
    query: createIdentificationStatusQueryOptions(staleTime),
  });

  usePrefetchPortalSimList();

  const { data: evnData } = useGetEvnForCustomer(undefined, {
    query: { refetchOnWindowFocus: false },
  });
  const { data: customer } = useGetCustomer();
  const showEVN = hasAvailableEvns(evnData);

  const kycDone = identificationStatus
    ? isKycVerified(identificationStatus)
    : false;
  const { isAtRiskOfRemoval } = useFWBMemberStatus();
  const showFWB =
    !!customer &&
    !isBusinessCustomer(customer) &&
    kycDone &&
    !isAtRiskOfRemoval;

  return (
    <div className="flex flex-col gap-6 pb-8">
      {/* From `desktop` up the same list sits at the bottom of the Sidebar,
          per design — not in the content column. */}
      <CockpitTodosSection className="desktop:hidden" />

      {/* CockpitPurchaseIntentSection renders nothing itself once
          its own basket data confirms there's nothing pending. */}
      <CockpitPurchaseIntentSection
        manageRoute={portalRoutes.manageDevice.base}
      />

      <CockpitAbosSection manageRoute={portalRoutes.manageDevice.base} />

      <CardGrid>
        <AddDeviceTile addDeviceRoute={portalRoutes.addDevice.base} />
        {showFWB && <FWBCard />}
        {showEVN && <EVNCard />}
      </CardGrid>
    </div>
  );
};
